import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from '../components/Users';
import { AutoResolve } from '../components/AutoResolve';
import { api, ApiError, type AdminMarket, type ResolutionSpec } from '../lib/api';
import { relativeTime } from '../lib/format';

/**
 * The operator's screen.
 *
 * Everything an administrator needs to run the exchange, so nobody has to touch
 * a terminal to list or resolve a market. The people running this are not
 * necessarily developers, and a market resolved by hand-editing a request is a
 * market resolved without the guard rails.
 *
 * The screen deliberately mirrors the lifecycle rather than hiding it: a market
 * moves DRAFT → OPEN → CLOSED → PROPOSED → RESOLVED, and each state offers only
 * the actions that state permits. The server enforces all of it regardless —
 * this just stops you being offered a button that would be refused.
 */
export function Admin() {
  const qc = useQueryClient();
  const markets = useQuery({ queryKey: ['admin-markets'], queryFn: api.adminMarkets });
  const [tab, setTab] = useState<'markets' | 'new' | 'users' | 'invites'>('markets');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin-markets'] });
    void qc.invalidateQueries({ queryKey: ['markets'] });
  };

  return (
    <div className="stack">
      <h1 className="question">Admin</h1>

      <div className="sidetoggle" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <button data-active={tab === 'markets'} onClick={() => setTab('markets')}>
          <b>Markets</b>
          <small>{markets.data?.length ?? 0} total</small>
        </button>
        <button data-active={tab === 'new'} onClick={() => setTab('new')}>
          <b>New market</b>
          <small>create and publish</small>
        </button>
        <button data-active={tab === 'users'} onClick={() => setTab('users')}>
          <b>People</b>
          <small>balances and access</small>
        </button>
        <button data-active={tab === 'invites'} onClick={() => setTab('invites')}>
          <b>Invites</b>
          <small>let people in</small>
        </button>
      </div>

      {tab === 'users' && <Users />}
      {tab === 'new' && <NewMarket onCreated={refresh} />}
      {tab === 'invites' && <Invites />}
      {tab === 'markets' && (
        <>
          {markets.isLoading && <div className="empty-state">Loading…</div>}
          {markets.data?.length === 0 && (
            <div className="empty-state">No markets yet. Create one to get started.</div>
          )}
          {markets.data?.map((m) => <MarketAdmin key={m.id} market={m} onChanged={refresh} />)}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const CRITERION_PLACEHOLDER =
  'Resolves YES if [the precise, checkable thing] is reported by [the named source] before [the deadline]. Resolves NO otherwise. If the source has not published by the resolution date, the market is voided.';

function NewMarket({ onCreated }: { onCreated: () => void }) {
  const [question, setQuestion] = useState('');
  const [criterion, setCriterion] = useState('');
  const [source, setSource] = useState('');
  const [closesAt, setClosesAt] = useState('');
  const [spec, setSpec] = useState<ResolutionSpec | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const closes = new Date(closesAt);
      // The resolution deadline defaults to three days after close: long enough
      // for a source to publish, short enough that credits are not held
      // hostage. Past it, the market voids itself rather than hanging.
      const resolves = new Date(closes.getTime() + 3 * 86_400_000);
      const { id } = await api.createMarket({
        question: question.trim(),
        criterion: criterion.trim(),
        resolutionSource: source.trim(),
        opensAt: new Date().toISOString(),
        closesAt: closes.toISOString(),
        resolvesBy: resolves.toISOString(),
        ...(spec ? { resolutionSpec: spec } : {}),
      });
      return id;
    },
    onSuccess: (id) => {
      setCreated(id);
      setError(null);
      onCreated();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the market.'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.publishMarket(id),
    onSuccess: () => {
      setCreated(null);
      setQuestion('');
      setCriterion('');
      setSource('');
      setClosesAt('');
      onCreated();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not publish.'),
  });

  const ready =
    question.trim().length >= 10 &&
    criterion.trim().length >= 20 &&
    source.trim().length >= 3 &&
    closesAt !== '' &&
    new Date(closesAt).getTime() > Date.now();

  if (created) {
    return (
      <div className="card">
        <h2>Draft created</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          It is saved but not visible to anyone yet. Read the criterion once more before publishing —
          <b> it is frozen the moment you publish</b>, and a market whose rules are wrong has to be
          voided and relisted rather than corrected.
        </p>
        <div className="criterion" style={{ marginTop: 12 }}>
          <dt>Question</dt>
          <dd>{question}</dd>
          <dt>Resolves</dt>
          <dd>{criterion}</dd>
          <dt>Source</dt>
          <dd>{source}</dd>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="submit" data-side="YES" style={{ marginTop: 0 }} disabled={publish.isPending} onClick={() => publish.mutate(created)}>
            {publish.isPending ? 'Publishing…' : 'Publish — open for trading'}
          </button>
          <button onClick={() => setCreated(null)}>Keep as draft</button>
        </div>
        {error && <div className="notice error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="card">
      <h2>New market</h2>

      <div className="field">
        <label htmlFor="q">Question</label>
        <input id="q" value={question} onChange={(e) => setQuestion(e.target.value)}
          placeholder="Will the ECB cut its deposit facility rate at the September 2026 meeting?" />
        <div className="hint">A yes-or-no question with one clear reading. Avoid "soon", "significantly", "most".</div>
      </div>

      <div className="field">
        <label htmlFor="c">How it resolves</label>
        <textarea id="c" rows={5} value={criterion} onChange={(e) => setCriterion(e.target.value)}
          placeholder={CRITERION_PLACEHOLDER}
          style={{
            font: 'inherit', width: '100%', background: 'var(--plane)', color: 'var(--ink)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 12px',
            lineHeight: 1.5, resize: 'vertical',
          }} />
        <div className="hint">
          This is the contract. Name the source, say exactly what makes it YES, and say what happens
          if the source never publishes. {criterion.trim().length}/20 characters minimum.
        </div>
      </div>

      <div className="field">
        <label htmlFor="s">Source</label>
        <input id="s" value={source} onChange={(e) => setSource(e.target.value)}
          placeholder="https://www.ecb.europa.eu/press/pr/date/2026/html/index.en.html" />
        <div className="hint">Where you will look to decide. Users see this before they trade.</div>
      </div>

      <AutoResolve spec={spec} onChange={setSpec} />

      <div className="field">
        <label htmlFor="t">Trading closes</label>
        <input id="t" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
        <div className="hint">
          Trading stops here automatically. You then have three days to propose a result before the
          market voids itself.
        </div>
      </div>

      <button className="submit" data-side="YES" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
        {create.isPending ? 'Creating…' : 'Create draft'}
      </button>
      {error && <div className="notice error">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------

function MarketAdmin({ market, onChanged }: { market: AdminMarket; onChanged: () => void }) {
  const [outcome, setOutcome] = useState<'YES' | 'NO' | 'VOID'>('YES');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = (fn: () => Promise<unknown>) => async () => {
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}` : 'That did not work.');
    }
  };

  const disputeOver =
    market.dispute_ends_at !== null && new Date(market.dispute_ends_at).getTime() <= Date.now();

  return (
    <div className="card">
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span className={`badge ${market.status === 'OPEN' ? 'open' : market.status === 'SETTLED' ? 'settled' : 'closed'}`}>
          {market.status}
        </span>
        <b style={{ fontSize: 15, flex: 1, minWidth: 220 }}>{market.question}</b>
        <Link to={`/markets/${market.id}`} className="muted" style={{ fontSize: 13 }}>View →</Link>
      </div>

      <div className="meta" style={{ marginTop: 10, marginBottom: 12 }}>
        <span>{market.open_interest.toLocaleString()} contracts</span>
        {market.last_price !== null && <span>{market.last_price}% YES</span>}
        {market.status === 'OPEN' && <span>closes {relativeTime(market.closes_at)}</span>}
        {market.outcome && <span>resolved <b>{market.outcome}</b></span>}
      </div>

      {market.status === 'DRAFT' && (
        <>
          <p className="hint">Not visible to anyone yet. Publishing freezes the criterion.</p>
          <button className="submit" data-side="YES" onClick={run(() => api.publishMarket(market.id))}>
            Publish
          </button>
        </>
      )}

      {market.status === 'OPEN' && (
        <>
          <p className="hint">
            Trading closes by itself at the scheduled time. Only close early if the outcome is already
            public — anyone still holding an order loses the chance to react.
          </p>
          <button onClick={run(() => api.closeMarket(market.id, true))}>Close trading now</button>
        </>
      )}

      {market.status === 'CLOSED' && (
        <>
          <h2 style={{ marginTop: 4 }}>Propose the result</h2>
          <div className="sidetoggle" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {(['YES', 'NO', 'VOID'] as const).map((o) => (
              <button key={o} data-side={o === 'NO' ? 'NO' : 'YES'} data-active={outcome === o}
                onClick={() => setOutcome(o)}>
                <b>{o}</b>
              </button>
            ))}
          </div>
          <div className="field">
            <label htmlFor={`ev-${market.id}`}>Evidence</label>
            <input id={`ev-${market.id}`} value={evidence} onChange={(e) => setEvidence(e.target.value)}
              placeholder="The ECB decision published 12 Sept states a 25bp cut" />
            <div className="hint">
              Recorded permanently and shown if anyone disputes. Say what the source actually said.
            </div>
          </div>
          <p className="hint">
            Nothing is paid out yet. Proposing starts a 24-hour window in which any user can object.
          </p>
          <button className="submit" data-side="YES" disabled={evidence.trim().length === 0}
            onClick={run(() => api.proposeResolution(market.id, outcome, evidence.trim()))}>
            Propose {outcome}
          </button>
        </>
      )}

      {market.status === 'PROPOSED' && (
        <>
          <div className="criterion">
            <dt>Proposed</dt>
            <dd><b>{market.proposed_outcome}</b></dd>
            <dt>Dispute window</dt>
            <dd>
              {market.dispute_ends_at
                ? disputeOver ? 'closed — ready to finalise' : `open, ends ${relativeTime(market.dispute_ends_at)}`
                : 'unknown'}
            </dd>
          </div>
          {market.open_disputes > 0 && (
            <div className="notice error" style={{ marginTop: 12 }}>
              {market.open_disputes} open dispute{market.open_disputes > 1 ? 's' : ''}. Finalising is
              blocked until they are judged — deliberately. Read the objection before deciding.
            </div>
          )}
          <p className="hint" style={{ marginTop: 12 }}>
            Finalising pays out. Once money has moved, unwinding it means compensating ledger entries
            and a public correction — far more painful than waiting.
          </p>
          <button className="submit" data-side="YES"
            disabled={!disputeOver || market.open_disputes > 0}
            onClick={run(() => api.finaliseMarket(market.id))}>
            {market.open_disputes > 0 ? 'Blocked by open disputes'
              : disputeOver ? 'Finalise and pay out' : 'Waiting for the dispute window'}
          </button>
        </>
      )}

      {(market.status === 'CLOSED' || market.status === 'PROPOSED') && (
        <details style={{ marginTop: 14 }}>
          <summary className="muted" style={{ cursor: 'pointer', fontSize: 13 }}>Void this market</summary>
          <p className="hint" style={{ marginTop: 8 }}>
            Everyone gets 50 back per contract, whichever side they took. Use when the criterion turned
            out ambiguous, the source never published, or the event was cancelled.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {(['CRITERION_AMBIGUOUS', 'SOURCE_UNAVAILABLE', 'EVENT_CANCELLED'] as const).map((r) => (
              <button key={r} onClick={run(() => api.voidMarket(market.id, r))} style={{ fontSize: 13 }}>
                {r.toLowerCase().replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </details>
      )}

      {error && <div className="notice error">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Invites() {
  const [emails, setEmails] = useState('');
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = emails
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter((e) => e.includes('@'));

  const send = useMutation({
    mutationFn: () => api.invite(list),
    onSuccess: (r) => {
      setDone(r.invited);
      setEmails('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not add the invites.'),
  });

  return (
    <div className="card">
      <h2>Invites</h2>
      <p className="hint" style={{ marginBottom: 14 }}>
        While the site is invite-only, only these addresses can sign in. Adding someone does not email
        them — they request a sign-in link themselves and it will now work.
      </p>
      <div className="field">
        <label htmlFor="inv">Email addresses</label>
        <textarea id="inv" rows={4} value={emails} onChange={(e) => setEmails(e.target.value)}
          placeholder="one@example.com, two@example.com"
          style={{
            font: 'inherit', width: '100%', background: 'var(--plane)', color: 'var(--ink)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '9px 12px',
            resize: 'vertical',
          }} />
        <div className="hint">Separated by commas, spaces or new lines. {list.length} recognised.</div>
      </div>
      <button className="submit" data-side="YES" disabled={list.length === 0 || send.isPending}
        onClick={() => send.mutate()}>
        {send.isPending ? 'Adding…' : `Invite ${list.length || ''}`.trim()}
      </button>
      {done !== null && <div className="notice ok">{done} address{done === 1 ? '' : 'es'} invited.</div>}
      {error && <div className="notice error">{error}</div>}
    </div>
  );
}
